import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { User, Gem } from 'lucide-react'
import { motion, AnimatePresence } from 'motion/react'
import audioService from '../../../services/audioService'
import { hapticImpact } from '../../../utils/haptic'
import ProfileLink from '../../../components/ProfileLink'
import TeamTag from '../../../components/TeamTag'

// Константы для рулетки
const TOTAL_ROULETTE_ITEMS = 20 // 15 рандомных + 1 выигрышный (16-й) + 4 рандомных
const WINNING_POSITION = 15 // Позиция выигрышного скина (16-й элемент, индекс 15)
const ROULETTE_SPIN_DURATION = 3000 // Длительность прокрутки в миллисекундах
const ROULETTE_FREEZE_DURATION = 1000 // Пауза на выигрышном скине перед показом результата
export const BATTLE_ROULETTE_DURATION = ROULETTE_SPIN_DURATION + ROULETTE_FREEZE_DURATION
const ITEM_WIDTH = 80 // Ширина одного элемента в пикселях

const BattleSlot = ({
  player,
  index,
  isWinner,
  caseSkins = [],
  shouldStartRoulette = false,
  rouletteElapsed = 0,
  rouletteDuration = BATTLE_ROULETTE_DURATION,
  onRouletteComplete,
  // Цвет банды игрока (только для банд с 2+ участниками в батле) — кромка слота
  // показывает, какие слоты заняты одной бандой, ещё до розыгрыша
  teamColor = null,
  // Личная доля банка кристаллами (`players[].team_payout`). Прокидывается
  // только после показа результата, иначе исход утечёт раньше рулетки
  teamPayout = 0
}) => {
  const { t } = useTranslation()
  
  // Состояния для рулетки
  const [rouletteItemsList, setRouletteItemsList] = useState([])
  const [isAnimating, setIsAnimating] = useState(false)
  const [showFinalResult, setShowFinalResult] = useState(false)
  const [isReady, setIsReady] = useState(false) // Флаг готовности к показу (скрывает результат до запуска рулетки)
  const rouletteRefs = useRef([])
  const spinAnimationsRef = useRef([])
  const spinTimeoutRef = useRef(null)
  const freezeTimeoutRef = useRef(null)

  // Функция генерации массива скинов для рулетки
  const generateRouletteItems = (winningItem) => {
    if (!winningItem || !caseSkins || caseSkins.length === 0) {
      return []
    }

    const items = []

    // Генерируем 15 рандомных скинов до выигрышного (позиции 0-14)
    for (let i = 0; i < WINNING_POSITION; i++) {
      const randomSkin = caseSkins[Math.floor(Math.random() * caseSkins.length)]
      items.push(randomSkin)
    }

    // Добавляем выигрышный скин на 16-ю позицию (индекс 15)
    items.push(winningItem)

    // Добавляем оставшиеся рандомные скины после выигрышного
    while (items.length < TOTAL_ROULETTE_ITEMS) {
      const randomSkin = caseSkins[Math.floor(Math.random() * caseSkins.length)]
      items.push(randomSkin)
    }

    return items
  }

  // Получаем список выигрышных скинов (поддержка нескольких открытий)
  const getWinningSkins = () => {
    if (!player) return []
    if (Array.isArray(player.skins) && player.skins.length > 0) {
      return player.skins
    }
    if (player.result) {
      return [player.result]
    }
    return []
  }

  // useEffect для генерации рулетки при появлении результата
  // НЕ генерируем рулетку если shouldStartRoulette = false (статус finished)
  useEffect(() => {
    const winningSkins = getWinningSkins()

    if (winningSkins.length > 0 && caseSkins.length > 0 && !isAnimating && !showFinalResult) {
      if (shouldStartRoulette) {
        // Статус raffle - генерируем рулетки для каждого выигрыша
        const itemsList = winningSkins.map((skin) => generateRouletteItems(skin))
        rouletteRefs.current = [] // Сбрасываем refs под актуальное количество рулеток
        setRouletteItemsList(itemsList)
        setIsReady(true) // Помечаем что рулетки готовы к показу
      } else {
        // Статус finished - показываем результат сразу без рулетки
        setIsReady(true)
        setShowFinalResult(true)
      }
    }

    if (winningSkins.length === 0) {
      // Сбрасываем состояние, если результатов пока нет
      rouletteRefs.current = [] // Сбрасываем refs при очистке
      setRouletteItemsList([])
      setShowFinalResult(false)
      setIsReady(false)
    }
  }, [player?.result, player?.skins, caseSkins, shouldStartRoulette])

  // useEffect для запуска анимации рулетки
  useEffect(() => {
    if (shouldStartRoulette && rouletteItemsList.length > 0 && !isAnimating && !showFinalResult) {
      startRouletteAnimation()
    }
  }, [shouldStartRoulette, rouletteItemsList])

  // Функция запуска анимации рулетки
  const startRouletteAnimation = () => {
    if (isAnimating) return

    const refsToAnimate = rouletteRefs.current.filter(Boolean)
    if (refsToAnimate.length === 0) return

    const spinDuration = Math.min(ROULETTE_SPIN_DURATION, Math.max(0, rouletteDuration - ROULETTE_FREEZE_DURATION))
    const revealDuration = Math.min(rouletteDuration, spinDuration + ROULETTE_FREEZE_DURATION)
    const elapsed = Math.max(0, rouletteElapsed)
    if (elapsed >= revealDuration) {
      setShowFinalResult(true)
      return
    }
    setIsAnimating(true)
    if (elapsed < 500) {
      hapticImpact('medium')
      audioService.play('caseBattleRouletteStart')
    }

    refsToAnimate.forEach((rouletteEl) => {
      const containerWidth = rouletteEl.offsetWidth
      const centerOffset = containerWidth / 2 - ITEM_WIDTH / 2
      const winningItemOffset = WINNING_POSITION * ITEM_WIDTH
      const finalPosition = winningItemOffset - centerOffset

      // Применяем исходную кривую прокрутки с учётом прошедшего времени
      if (elapsed >= spinDuration) {
        rouletteEl.style.transform = `translateX(-${finalPosition}px)`
        return
      }
      const animation = rouletteEl.animate(
        [{ transform: 'translateX(0)' }, { transform: `translateX(-${finalPosition}px)` }],
        { duration: spinDuration, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)', fill: 'forwards' }
      )
      animation.currentTime = elapsed
      spinAnimationsRef.current.push(animation)
    })

    // После завершения прокрутки делаем паузу на выигрышных скинах,
    // и только после паузы показываем финальный результат
    spinTimeoutRef.current = setTimeout(() => {
      freezeTimeoutRef.current = setTimeout(() => {
        setIsAnimating(false)
        setShowFinalResult(true)
        if (elapsed < 500) {
          hapticImpact('heavy')

          // Звук появления скина зависит от слота игрока (1..4).
          // Для слотов > 4 используем последний доступный (Item_Appear_4) как фолбэк.
          const appearSlot = Math.min(Math.max((index ?? 0) + 1, 1), 4)
          audioService.play(`caseBattleItemAppear${appearSlot}`)
        }

        // Вызываем коллбэк о завершении анимации
        if (onRouletteComplete) {
          onRouletteComplete()
        }
      }, Math.max(0, revealDuration - Math.max(elapsed, spinDuration)))
    }, Math.max(0, spinDuration - elapsed))
  }

  // Очистка таймера при размонтировании
  useEffect(() => {
    const animations = spinAnimationsRef.current
    return () => {
      if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current)
      if (freezeTimeoutRef.current) clearTimeout(freezeTimeoutRef.current)
      animations.forEach((animation) => animation.cancel())
    }
  }, [])

  if (!player) {
    return (
      <div className="relative rounded-xl p-4 border border-text-secondary/20 bg-gradient-to-br from-surface/50 to-dark-primary/50 backdrop-blur-sm flex flex-col items-center justify-center min-h-[180px] overflow-hidden">
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-text-secondary/5 to-transparent" />
        
        {/* Content */}
        <div className="relative flex flex-col items-center">
          <div className="w-16 h-16 rounded-full bg-surface/50 flex items-center justify-center mb-3 border border-text-secondary/20">
            <User className="w-8 h-8 text-text-secondary/50" />
          </div>
          <p className="text-text-secondary text-sm text-center w-full">
            {t('battle.waitingPlayer')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`
        relative rounded-xl p-4 border transition-all flex flex-col justify-center min-h-[180px] overflow-hidden
        ${isWinner 
          ? 'border-accent-primary bg-gradient-to-br from-accent-primary/20 to-accent-primary/5 shadow-lg shadow-accent-primary/30' 
          : 'border-accent-primary/40 bg-gradient-to-br from-accent-primary/10 via-surface/60 to-accent-secondary/10 backdrop-blur-sm shadow-lg shadow-accent-primary/20'
        }
      `}
      style={teamColor ? { borderLeftColor: teamColor, borderLeftWidth: '3px' } : undefined}
    >
      {/* Светящийся эффект для обычных игроков */}
      {!isWinner && (
        <div className="absolute inset-0 bg-gradient-to-br from-accent-primary/5 to-transparent animate-pulse-slow" />
      )}
      
      {/* Subtle gradient overlay for winner */}
      {isWinner && (
        <div className="absolute inset-0 bg-gradient-to-br from-accent-primary/10 to-transparent animate-pulse-slow" />
      )}
      
      {/* Content */}
      <div className="relative">

      {/* Player Avatar — кликабелен при user_slug !== null */}
      <ProfileLink slug={player.user_slug} className="flex justify-center mb-3">
        <img
          src={player.user_ava || `https://ui-avatars.com/api/?name=${player.user_name}&background=00FF7F&color=0A0A0F`}
          alt={player.user_name}
          className={`
            w-16 h-16 rounded-full border-2
            ${isWinner ? 'border-accent-primary' : 'border-accent-primary/60'}
          `}
        />
      </ProfileLink>

      {/* Player Name — кликабелен при user_slug !== null */}
      <div className="mb-3">
        {/* В кейсбатлах банда приходит в user_team. Бейдж — отдельной строкой
            над ником; при `user_team === null` строки нет вовсе, иначе появился
            бы пустой отступ. `pl-1` гасит правый отступ бейджа, нужный ему
            только когда он стоит вплотную перед ником в одну строку. Слот
            узкий, поэтому длинное название обрезается по ширине (`truncate`) */}
        {player.user_team && (
          <p className="text-sm font-medium text-center truncate">
            <TeamTag team={player.user_team} className="pl-1" />
          </p>
        )}
        <p className="text-white font-medium text-center truncate">
          <ProfileLink slug={player.user_slug}>
            {player.user_name || `Player #${index + 1}`}
          </ProfileLink>
        </p>
        {showFinalResult && getWinningSkins().length > 1 && (
          <div className="flex items-center justify-center gap-1 text-accent-primary text-sm font-bold mt-1">
            <Gem className="w-4 h-4" />
            <span>{getWinningSkins().reduce((sum, skin) => sum + (Number(skin?.price) || 0), 0).toLocaleString()}</span>
          </div>
        )}
        {/* Доля банка кристаллами: тиммейт с долей никогда не подписан как
            проигравший, а у слота-победителя доля на 1–3 кристалла больше */}
        {teamPayout > 0 && (
          <div className="mt-1 flex justify-center">
            <span className="inline-flex items-center gap-1 rounded-full border border-accent-primary/40 bg-accent-primary/15 px-2 py-0.5 text-xs font-bold tabular-nums text-accent-primary">
              <Gem className="h-3 w-3" />
              +{teamPayout.toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {/* Рулетка или финальный результат */}
      {(getWinningSkins().length > 0) && (isReady || showFinalResult) && (
        <div className="bg-dark-primary rounded-lg p-3">
          <AnimatePresence mode="wait">
            {/* Показываем рулетки если есть скины и анимация не завершена (статус raffle) */}
            {rouletteItemsList.length > 0 && !showFinalResult ? (
              <motion.div
                key="roulette"
                className="space-y-3"
                initial="enter"
                animate="center"
                exit="exit"
                variants={{
                  enter: { opacity: 0, rotateY: -90, scale: 0.5 },
                  center: {
                    opacity: 1,
                    rotateY: 0,
                    scale: 1,
                    transition: {
                      duration: 0.65,
                      ease: [0.34, 1.56, 0.64, 1],
                      opacity: { duration: 0.2 }
                    }
                  },
                  exit: {
                    opacity: 0,
                    scale: 0.9,
                    y: -12,
                    transition: {
                      duration: 0.15,
                      ease: 'easeOut',
                      opacity: { duration: 0.1 }
                    }
                  }
                }}
                style={{
                  transformStyle: 'preserve-3d',
                  perspective: '1000px'
                }}
              >
                {rouletteItemsList.map((rouletteItems, rouletteIndex) => (
                  <div key={`roulette-${rouletteIndex}`} className="relative w-full h-24 overflow-hidden">
                    {/* Индикатор центра (вертикальная линия) */}
                    <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-accent-primary z-10 transform -translate-x-1/2" />

                    {/* Контейнер рулетки */}
                    <div
                      ref={(el) => {
                        rouletteRefs.current[rouletteIndex] = el
                      }}
                      className="flex gap-0 h-full items-center"
                      style={{
                        transform: 'translateX(0)',
                        willChange: 'transform'
                      }}
                    >
                      {rouletteItems.map((skin, idx) => (
                        <div
                          key={`roulette-${rouletteIndex}-${idx}`}
                          className="flex-shrink-0 rounded-lg p-0 flex flex-col items-center justify-center"
                          style={{ width: `${ITEM_WIDTH}px`, height: '80px' }}
                        >
                          <img
                            src={skin.img}
                            alt={skin.name}
                            className="w-12 h-12 object-contain mb-1"
                          />
                          <div className="flex items-center gap-0.5 text-accent-primary text-xs font-bold">
                            <Gem className="w-3 h-3" />
                            <span>{skin.price?.toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </motion.div>
            ) : (
              // Финальный результат:
              // 1. После завершения рулеток (raffle с showFinalResult=true)
              // 2. Сразу при статусе finished (рулетки не запускались)
              <motion.div
                key="result"
                className="grid grid-cols-1 gap-3"
                initial={{ opacity: 0, scale: 0.5, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{
                  duration: 0.15,
                  ease: [0.34, 1.56, 0.64, 1],
                  opacity: { duration: 0.2 }
                }}
              >
                {getWinningSkins().map((skin, skinIndex) => (
                  <div key={`result-${skinIndex}`} className="flex flex-col items-center text-center">
                    <img
                      src={skin.img}
                      alt={skin.name}
                      className="w-32 h-32 aspect-square object-contain mb-2 mx-auto"
                    />
                    <p className="text-white text-xs font-medium text-center mb-1 w-full">
                      {skin.name}
                    </p>
                    <div className="flex items-center justify-center gap-1 text-accent-primary font-bold">
                      <Gem className="w-4 h-4" />
                      <span>{skin.price?.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      </div>
    </div>
  )
}

export default BattleSlot
